import { Avatar, Box } from '@mui/material';
import { useRecordContext } from 'react-admin';
import { MemberLight, MemberSummary, MemberType, Profile } from '@wwf/sdk';
import { Star, EmojiNature as StaffIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import { useResource } from '@app/misc/useResource';

export enum AvatarSize {
    Small = 30,
    Medium = 40,
    Large = 80,
    Big = 120,
    Huge = 160,
}

export interface MemberAvatarProps {
    record?: Profile | MemberLight | MemberSummary;
    width?: number;
    height?: number;
    title?: string;
    variant?: 'sm' | 'md' | 'lg' | 'xl';
    type?: MemberType;
    style?: React.CSSProperties;
}

export const MemberAvatar = (props: MemberAvatarProps) => {
    const record = useRecordContext<Profile | MemberLight | MemberSummary>(
        props
    );
    const { width, height, variant = 'md', type, style } = props;

    // Determine size based on props
    const avatarSize = (() => {
        if (width && height) return { width, height };
        switch (variant) {
            case 'sm':
                return { width: AvatarSize.Small, height: AvatarSize.Small };
            case 'md':
                return { width: AvatarSize.Medium, height: AvatarSize.Medium };
            case 'lg':
                return { width: AvatarSize.Large, height: AvatarSize.Large };
            case 'xl':
                return { width: AvatarSize.Big, height: AvatarSize.Big };
        }
    })();

    const _width =
        typeof avatarSize === 'number' ? avatarSize : avatarSize.width;
    const _height =
        typeof avatarSize === 'number' ? avatarSize : avatarSize.height;

    const { data: src } = useResource(record?.avatar?.id);

    const iconSize = useMemo(() => {
        return _width <= AvatarSize.Small
            ? 15
            : _width <= AvatarSize.Medium
              ? 20
              : _width <= AvatarSize.Large
                ? 30
                : 40;
    }, [_width]);

    // Icon overlay logic similar to React Native version
    const mainVolunteerIcon = useMemo(() => {
        return (
            <Star
                style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    fontSize: iconSize,
                    color: '#d7e112',
                }}
            />
        );
    }, [iconSize]);

    const staffIcon = useMemo(() => {
        return (
            <StaffIcon
                style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    fontSize: iconSize,
                    color: '#e40242',
                }}
            />
        );
    }, [iconSize]);

    // Determine which overlay icon to show
    const overlayIcon = (() => {
        const memberType = type || record?.type;

        switch (memberType) {
            case 'MAIN_VOLUNTEER':
                return mainVolunteerIcon;
            case 'VOLUNTEER':
                return null;
            case 'STAFF':
                return staffIcon;
            default:
                return null;
        }
    })();

    if (src) {
        return (
            <Box
                style={{
                    position: 'relative',
                    ...style,
                    width: _width,
                    height: _height,
                }}
            >
                <img
                    src={src}
                    alt={''}
                    style={{
                        width: _width,
                        height: _height,
                        borderRadius: '50%',
                        objectFit: 'cover',
                    }}
                />
                {overlayIcon}
            </Box>
        );
    }

    if (!record?.avatar && !record?.firstName && !record?.lastName) {
        return null;
    }

    return (
        <Box
            style={{
                position: 'relative',
                ...style,
                width: _width,
                height: _height,
            }}
        >
            <Avatar
                src={src ?? undefined}
                sx={{
                    width: _width,
                    height: _height,
                }}
                title={props.title}
            >
                {record.firstName?.charAt(0).toUpperCase()}
                {record.lastName?.charAt(0).toUpperCase()}
            </Avatar>
            {overlayIcon}
        </Box>
    );
};
